/* eslint-disable max-lines */
import {
  DatabaseAuto, IblockedReasons,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  Icompany,
  IdataArrayResponse,
  IdeleteOneResponse,
  Ifile, IfilterProps, Isuccess,
  TcompanyRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Represents a company entity in the system.
 * Extends DatabaseAuto to inherit common database operations.
 * Handles company data, validation, and business logic.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Company} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const company = await Company.getAll();
 *
 * const company = await Company.filterAll({
 *
 * });
 *
 * const company = await Company.bulkDelete({
 *
 * });
 *
 * const company = await Company.getOne('resourceId');
 *
 * const company = await Company.remove();
 *
 * ```
 */
export class Company extends DatabaseAuto {
  /** Base API endpoint for company operations */
  static readonly baseUrl = '/company';

  /** Unique reference ID for the company */
  readonly urId: Icompany['urId'];

  /** Legal name of the company */
  readonly name: Icompany['name'];

  /** Display name of the company (can differ from legal name) */
  readonly displayName?: Icompany['displayName'];

  /** Date when the company was established */
  readonly dateOfEst?: Icompany['dateOfEst'];

  /** Physical address of the company */
  readonly address?: Icompany['address'];

  /** Additional details about the company */
  readonly details?: Icompany['details'];

  /** Type of business/industry */
  readonly businessType?: Icompany['businessType'];

  /** Company's website URL */
  readonly websiteAddress?: Icompany['websiteAddress'];

  /** Reasons why the company was blocked (if applicable) */
  readonly blockedReasons?: IblockedReasons;

  /** Whether the company has left the platform */
  readonly left?: Icompany['left'];

  /** Date when the company left the platform */
  readonly dateLeft?: Icompany['dateLeft'];

  /** Whether the company is currently blocked */
  readonly blocked: Icompany['blocked'];

  /** Whether the company has been verified */
  readonly verified: Icompany['verified'];

  /** Owner of the company (can be user ID or user object) */
  readonly owner: IcommerceUser | string;

  /** Company's logo image */
  readonly logo: Icompany['logo'];

  /** Background image for company's profile */
  readonly logoBackgroundPic: Icompany['logoBackgroundPic'];

  /** Additional photos of the company */
  readonly photos: Icompany['photos'];

  readonly isPendingFirstSubscription: Icompany['isPendingFirstSubscription'];

  /**
   * Creates a new Company instance with validated data.
   * @param {Icompany} data - The company data to initialize with
   * @throws {Error} When validation of the input data fails
   */
  constructor(data: Icompany) {
    validateData(data, {
      urId: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' && v.trim().length > 0),
        message: 'urId must be a non-empty string'
      },
      name: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Company name must be a non-empty string '
      },
      displayName: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' && v.trim().length > 0),
        message: 'Display name must be a non-empty string'
      },
      dateOfEst: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (!isNaN(v instanceof Date ? v.getTime() : new Date(v).getTime())),
        message: 'Date of establishment must be a valid date (Date object or ISO date string)'
      },
      businessType: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' && v.trim().length > 0),
        message: 'Business type must be a non-empty string'
      },
      blocked: {
        validate: (v) => typeof v === 'boolean',
        message: 'Blocked must be a boolean'
      },
      verified: {
        required: false,
        validate: (v) => typeof v === 'boolean',
        message: 'Verified must be a boolean'
      },
      owner: {
        validate: (v) =>
          typeof v === 'string' ||
          (typeof v === 'object' && v !== null),
        message: 'Owner must be a string or a User object'
      }
    });

    super(data);
    this.urId = data.urId || '';
    this.name = data.name || 'Unknown';
    this.displayName = data.displayName || 'Unknown';
    if (data.dateOfEst) {
      this.dateOfEst = new Date(data.dateOfEst);
    }
    this.details = data.details || '';
    this.businessType = data.businessType || 'General';
    this.websiteAddress = data.websiteAddress || '';
    if (data.blockedReasons && Object.keys(data.blockedReasons).length > 0) {
      this.blockedReasons = data.blockedReasons;
    }
    this.left = data.left || false;
    if (data.dateLeft) {
      this.dateLeft = new Date(data.dateLeft);
    }
    this.blocked = data.blocked || false;
    this.verified = data.verified || false;
    this.address = data.address || '';
    this.owner = data.owner;

    this.logo = data.logo;
    this.logoBackgroundPic = data.logoBackgroundPic;
    this.photos = data.photos;
    this.isPendingFirstSubscription = data.isPendingFirstSubscription || false;
  }

  /**
   * Retrieves all companies with pagination.
   * @param {number} [offset=0] - Number of records to skip
   * @param {number} [limit=20] - Maximum number of records to return
   * @returns {Promise<{count: number, dataArr: Company[]}>} Paginated list of companies
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient
      .ehttp.makeGet<IdataArrayResponse<Icompany>>(`${Company.baseUrl}?${query}`);
    const companys = await lastValueFrom(observer$);

    return {
      count: companys.count,
      dataArr: companys.data.map(val => new Company(val))
    };
  }

  /**
   * Filters companies based on provided criteria.
   * @param {IfilterProps} filter - Filter criteria for companies
   * @returns {Promise<{count: number, dataArr: Company[]}>} Filtered list of companies
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient
      .ehttp.makePost<IdataArrayResponse<Icompany>>(`${Company.baseUrl}/filter`, filter);
    const companys = await lastValueFrom(observer$);

    return {
      count: companys.count,
      dataArr: companys.data.map(val => new Company(val))
    };
  }

  /**
   * Retrieves a single company by its ID.
   * @param {string} resourceId - The unique identifier of the company
   * @returns {Promise<Company>} The requested company
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp.makeGet<Icompany>(`${Company.baseUrl}/${resourceId}`);
    const company = await lastValueFrom(observer$);

    return new Company(company);
  }

  /**
   * Retrieves all companies associated with the current user.
   * @returns {Promise<Company[]>} Array of user's companies
   */
  static async myProfiles() {
    const observer$ = CommerceClient.ehttp.makePost<Icompany[]>(`${Company.baseUrl}/my-profiles`, {});
    const company = await lastValueFrom(observer$);

    return company.map(val => new Company(val));
  }

  /**
   * Adds a new company to the database.
   * @param {TcompanyRequest<true>} payload - The company data to create
   * @returns {Promise<{success: boolean, company: Icompany, isFirstTime: boolean}>}
   * Result of the creation operation
   */
  static add(payload: TcompanyRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<{ success: boolean; company: Icompany; isFirstTime: boolean }>(`${Company.baseUrl}`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes multiple companies in bulk.
   * @param {IbulkDelete} payload - Criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} Result of the bulk delete operation
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient
      .ehttp.makePost<IbulkDeleteSuccessResponse>(`${Company.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current company with new values.
   * @param {TcompanyRequest} payload - The data to update
   * @returns {Promise<Company>} The updated company
   */
  async update(payload: TcompanyRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<Icompany>(`${Company.baseUrl}/${this._id}`, payload);

    return new Company(await lastValueFrom(observer$));
  }

  /**
   * Uploads owner-related images to the company.
   * @param {Ifile[]} files - Array of image files to upload
   * @returns {Observable<Icompany>} Observable with the updated company
   */
  uploadOwnerImages(files: Ifile[]) {
    return CommerceClient.ehttp
      .uploadFiles<Icompany>(files, `${Company.baseUrl}/upload-owner-images`, { _id: this._id });
  }

  /**
   * Uploads the owner's profile picture.
   * @param {Ifile} file - The image file to upload
   * @returns {Observable<Icompany>} Observable with the updated company
   */
  uploadProfilePicture(file: Ifile) {
    return CommerceClient.ehttp
      .uploadFiles<Icompany>([file], `${Company.baseUrl}/upload-owner-profile-picture`, { _id: this._id });
  }

  /**
   * Uploads the owner's profile cover picture.
   * @param {Ifile} file - The image file to upload
   * @returns {Observable<Icompany>} Observable with the updated company
   */
  uploadProfileCoverPicture(file: Ifile) {
    return CommerceClient.ehttp
      .uploadFiles<Icompany>([file], `${Company.baseUrl}/upload-owner-profile-cover-picture`, { _id: this._id });
  }

  /**
   * Sets an uploaded image as the specified owner image type.
   * @param {string} fileId - ID of the uploaded file
   * @param {'profilePicture' | 'profileCoverPicture'} field - Which image field to set
   * @returns {Promise<Company>} The updated company
   */
  async setOwnerImageAs(fileId: string, field: 'profilePicture' | 'profileCoverPicture') {
    const observer$ = CommerceClient.ehttp
      .makePost<Icompany>(`${Company.baseUrl}/set-owner-picture-as`, { fileId, field });

    return new Company(await lastValueFrom(observer$));
  }

  /**
   * Deletes owner-related images from the company.
   * @param {string[]} fileIds - Array of file IDs to delete
   * @returns {Promise<Company>} The updated company
   */
  async deleteOwnerImages(fileIds: string[]) {
    const observer$ = CommerceClient.ehttp
      .makePost<Icompany>(`${Company.baseUrl}/delete-owner-images`, { fileIds, _id: this._id });

    return new Company(await lastValueFrom(observer$));
  }

  /**
   * Uploads general company images.
   * @param {Ifile[]} files - Array of image files to upload
   * @returns {Observable<Icompany>} Observable with the updated company
   */
  uploadImages(files: Ifile[]) {
    return CommerceClient.ehttp
      .uploadFiles<Icompany>(files, `${Company.baseUrl}/upload-images`, { _id: this._id });
  }

  /**
   * Uploads the company logo.
   * @param {Ifile} file - The logo image file
   * @returns {Observable<Icompany>} Observable with the updated company
   */
  uploadLogo(file: Ifile) {
    return CommerceClient.ehttp
      .uploadFiles<Icompany>([file], `${Company.baseUrl}/upload-logo`, { _id: this._id });
  }

  /**
   * Uploads the company's cover photo.
   * @param {Ifile} file - The cover image file
   * @returns {Observable<Icompany>} Observable with the updated company
   */
  uploadLogoCoverPicture(file: Ifile) {
    return CommerceClient.ehttp
      .uploadFiles<Icompany>([file], `${Company.baseUrl}/upload-logo-cover-picture`, { _id: this._id });
  }

  /**
   * Sets an uploaded image as the specified company image type.
   * @param {string} fileId - ID of the uploaded file
   * @param {'logo' | 'logoBackgroundPic'} field - Which image field to set
   * @returns {Promise<Company>} The updated company
   */
  async setImageAs(fileId: string, field: 'logo' | 'logoBackgroundPic') {
    const observer$ = CommerceClient.ehttp
      .makePost<Icompany>(`${Company.baseUrl}/set-image-as`, { fileId, field });

    return new Company(await lastValueFrom(observer$));
  }

  /**
   * Deletes company images.
   * @param {string[]} fileIds - Array of file IDs to delete
   * @returns {Promise<Company>} The updated company
   */
  async deleteImages(fileIds: string[]) {
    const observer$ = CommerceClient.ehttp
      .makePost<Icompany>(`${Company.baseUrl}/delete-images`, { fileIds, _id: this._id });

    return new Company(await lastValueFrom(observer$));
  }

  /**
   * Removes the current company from the database.
   * @returns {Promise<IdeleteOneResponse | null>} Result of the delete operation
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Company.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }

  /**
   * Changes the currency preference for the company.
   * @param {string} currency - The new currency code (e.g., 'USD', 'EUR')
   * @returns {Promise<Isuccess>} Result of the currency change operation
   */
  changeUserCurrency(currency: string) {
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Company.baseUrl}/change-currency`, { currency });

    return lastValueFrom(observer$);
  }
}
